import React from "react";
import LoadingSquare from "shared/components/loading/LoadingSquare";
import MiscOverall from "shared/components/misc/MiscOverall.js";
import MiscPlayerAge from "shared/components/misc/MiscPlayerAge.js";
import MiscPlayerChip from "shared/components/misc/MiscPlayerChip.js";
import PopupInformationPricing from "shared/components/popups/PopupInformationPricing";
import PopupPlayer from "shared/components/popups/PopupPlayer";
import { useIsSmall } from "shared/components/utils/UtilIsSmall.js";
import { analyzePlayerAttributes, getCalculatedOverall, getOverallTag, positions } from "shared/utils/player.js";
import "./Item.css";

interface ItemRowPlayerAssistProps {
  p: any;
  display: string;
  actualPosition?: string;
  isSelected: boolean;
  selectable: boolean;
  onSelect?: (p: any) => void;
  pricing?: number;
  onDelete?: func;
}

const ItemRowPlayerAssist: React.FC<ItemRowPlayerAssistProps> = ({
  p,
  display,
  actualPosition,
  isSelected,
  selectable,
  onSelect,
  pricing,
  onDelete
}) => {
  const { ref: containerRef, isSmall } = useIsSmall(600);

  const getOVRs = () => {
    return positions
      .map((pos) => {
        const calcOVR = getCalculatedOverall(p, pos.name);
        return {
          pos: pos.name,
          ovr: calcOVR,
          diff: calcOVR - p.overall,
        };
      })
      .filter((a) => a.ovr > 0 || a.pos === actualPosition)
      .sort((a, b) => Number(b.pos === actualPosition) - Number(a.pos === actualPosition) || b.ovr - a.ovr)
      .slice(0, 3);
  };

  const getDiff = (diff: number) => {
    if (diff === 0) return null;
    return (
      <div className={`d-inline-block ${diff > 0 ? "text-info" : "text-danger"}`}>{diff > 0 ? `+${diff}` : diff}</div>
    );
  };

  return (
    <div ref={containerRef} className={`Item d-flex flex-row flex-fill ${isSelected ? "selected" : ""}`}>
      {selectable && (
        <div className="d-flex align-items-center justify-content-center me-1">
          <input style={{marginTop: "0px"}} type="checkbox" checked={!!isSelected} onChange={() => (onSelect ? onSelect(p) : undefined)} />
        </div>
      )}

      <PopupPlayer
        player={p}
        trigger={
          <div className={`d-flex ${isSmall ? "flex-column" : "flex-row"} flex-fill pb-1 pb-md-0`}>
            <div className={`d-flex flex-row ${isSmall ? "" : "flex-md-basis-340"}`}>
              <i className="bi bi-person-badge-fill me-1" />

              <div className="d-flex flex-fill min-width-0">
                <div className="text-truncate w-100">
                  {p.firstName} {p.lastName}
                </div>
              </div>

              <div className="d-flex flex-basis-40">
                <MiscOverall player={p} />
              </div>

              <div className="d-flex flex-basis-120">{p.positions.join(", ")}</div>
            </div>

            <div className="d-flex flex-md-grow-1">
              {/* Switch trigger content based on container width */}
              <>
                {(!display || display === "profile") && (
                  <div className="d-flex flex-grow-1">
                    <MiscPlayerAge age={p.age} retirementYears={p.retirementYears} />

                    <div className={"d-inline-block"} style={{ width: "50px" }}>
                      <i className="bi bi-rulers me-1"></i>
                      {p.height}
                    </div>

                    {p.nationalities && p.nationalities[0] ? (
                      <img
                        className="d-inline me-1 my-1 ms-md-1"
                        style={{ height: 13 }}
                        src={`https://app.playmfl.com/img/flags/${p.nationalities[0]}.svg`}
                      />
                    ) : null}

                    {p.nationalities && p.nationalities[0]
                      ? p.nationalities[0].length > 10
                        ? `${p.nationalities[0].substring(0, 10)}...`
                        : p.nationalities[0]
                      : ""}
                  </div>
                )}

                {display === "stats" && (
                  <div className="d-flex flex-grow-1">
                    {["pace", "shooting", "passing", "dribbling", "defense", "physical"].map((stat) => (
                      <div className="me-1" style={{ width: "38px" }} key={stat}>
                        {stat[0].toUpperCase()}: {getOverallTag(p[stat])}
                      </div>
                    ))}
                  </div>
                )}

                {display === "ovr" && (
                  <div className="d-flex flex-grow-1">
                    {getOVRs().map((o) => (
                      <div className="me-1" key={o.pos}>
                        <div className="d-inline-block" style={{ width: "38px" }}>
                          {o.pos}:
                        </div>
                        <div className="d-inline-block" style={{ width: "40px" }}>
                          {getOverallTag(o.ovr)}
                          <small>{getDiff(o.diff)}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {display === "owner" && (
                  <div className="d-flex flex-grow-1">
                    <div className="me-1">
                      <i className="bi bi-person-fill me-1" />
                      {p.owner?.name || "???"}
                    </div>
                  </div>
                )}

                {display === "pricing" && (
                  <div className="d-flex flex-grow-1 pe-1">
                    {pricing ? (
                      <div className="d-flex flex-row">
                        <MiscPlayerAge age={p.age} retirementYears={p.retirementYears} />&nbsp;
                        <PopupInformationPricing />
                        &nbsp;
                        <span className="text-main">${pricing}</span>
                      </div>
                    ) : (
                      <LoadingSquare />
                    )}
                  </div>
                )}

                {display === "traits" && (
                  <div className="d-flex flex-grow-1 text-xs">
                    {analyzePlayerAttributes(p).map((c) => (
                      <MiscPlayerChip info={c} />
                    ))}
                  </div>
                )}
              </>
            </div>
          </div>
        }
      />
      {onDelete &&
        <div className="d-flex justify-content-center">
          <button
            className="btn btn-small text-danger"
            onClick={() => onDelete()}
          >
            <i className="bi bi-x-circle" />
          </button>
        </div>
      }
    </div>
  );
};

export default ItemRowPlayerAssist;
